import { useState, type ReactNode } from "react"
import { motion, AnimatePresence } from "framer-motion"
import { toast } from "sonner"
import { useAnalytics } from "@/hooks/useAnalytics"
import {
  createCalendarEvent,
  type CalendarEventInput,
  type CalendarEventRow,
  type CalendarEventType,
} from "@/lib/supabase"

/**
 * Parcours d'ajout d'un événement au calendrier (séance libre, activité sportive,
 * récupération active) : titre, puis heure de fin, puis — séance libre seulement —
 * contenu de la séance, facultatif.
 *
 * Partagé entre le calendrier (jour choisi dans la grille) et, pour la séance libre,
 * l'accueil et l'historique des comptes en suivi seul (jour = aujourd'hui) : le même
 * parcours partout, pas trois copies qui divergent.
 */

/** Longueur maximale du contenu — même borne que la contrainte en base. */
export const FREE_SESSION_CONTENT_MAX = 5000

const CYAN_BUTTON_STYLE = { background: "linear-gradient(135deg,#0369a1,#06b6d4)", boxShadow: "0 4px 16px rgba(6,182,212,0.25)" }
const GHOST_BUTTON_STYLE = { border: "1px solid rgba(255,255,255,0.08)", background: "rgba(255,255,255,0.03)" }

export function formatDateLong(iso: string): string {
  try {
    const [y, m, d] = iso.split("-")
    const dt = new Date(parseInt(y), parseInt(m) - 1, parseInt(d))
    return dt.toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" })
      .replace(/^./, (s) => s.toUpperCase())
  } catch {
    return iso
  }
}

// ============================================
// SHEET (fond + panneau du bas)
// ============================================

export function CalendarSheet({ open, onClose, children }: { open: boolean; onClose: () => void; children: ReactNode }) {
  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[90] bg-black/60 backdrop-blur-[4px]"
            onClick={onClose} />

          <motion.div initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }}
            transition={{ type: "spring", stiffness: 350, damping: 30 }}
            className="fixed z-[100] bottom-0 left-0 right-0 p-5 pb-8 rounded-t-3xl max-h-[85vh] overflow-y-auto"
            style={{ background: "linear-gradient(180deg,#0f1829,#0a1020)", border: "1px solid rgba(30,41,59,0.55)" }}>

            {/* Close handle */}
            <div className="flex justify-center mb-3">
              <div className="w-10 h-1 rounded-full bg-white/10" />
            </div>

            {children}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  )
}

// ============================================
// ÉTAPES D'AJOUT
// ============================================

export interface AddEventValues {
  title: string
  /** HH:MM:00 */
  endTime: string
  /** Séance libre seulement ; null = étape passée ou laissée vide. */
  content: string | null
}

interface AddEventStepsProps {
  date: string
  type: CalendarEventType
  /** Libellé du type (« Séance libre »…), titre de la première étape. */
  typeLabel: string
  saving: boolean
  onCancel: () => void
  onSubmit: (values: AddEventValues) => void
}

type Step = "name" | "time" | "content"

export function AddEventSteps({ date, type, typeLabel, saving, onCancel, onSubmit }: AddEventStepsProps) {
  const [step, setStep] = useState<Step>("name")
  const [nameInput, setNameInput] = useState("")
  const [hourInput, setHourInput] = useState("12")
  const [minuteInput, setMinuteInput] = useState("00")
  const [contentInput, setContentInput] = useState("")

  const hasContentStep = type === "seance-libre"
  const endTime = `${hourInput.padStart(2, "0")}:${minuteInput.padStart(2, "0")}:00`

  const handleSubmitName = () => {
    if (!nameInput.trim()) {
      toast.error("Nom requis")
      return
    }
    setStep("time")
  }

  const submit = (content: string | null) => {
    const trimmed = content?.trim() ?? ""
    onSubmit({ title: nameInput.trim(), endTime, content: trimmed ? trimmed : null })
  }

  const handleSubmitTime = () => {
    if (hasContentStep) setStep("content")
    else submit(null)
  }

  if (step === "name") {
    return (
      <>
        <h3 className="text-[18px] font-bold text-white mb-1">{typeLabel}</h3>
        <p className="text-[13px] text-gray-500 mb-4">{formatDateLong(date)}</p>
        <div className="flex items-center gap-3 px-4 h-[48px] rounded-xl mb-4"
          style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(30,41,59,0.55)" }}>
          <input
            type="text"
            value={nameInput}
            onChange={(e) => setNameInput(e.target.value)}
            placeholder="Ex: Football, Yoga, Marche…"
            autoFocus
            className="flex-1 bg-transparent outline-none text-[14px] text-white placeholder:text-gray-600"
            style={{ caretColor: "#06b6d4" }}
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <button onClick={onCancel}
            className="py-3 rounded-xl text-[14px] font-semibold text-gray-400 cursor-pointer transition-all active:scale-[0.97]"
            style={GHOST_BUTTON_STYLE}>
            Annuler
          </button>
          <button onClick={handleSubmitName} disabled={!nameInput.trim()}
            className="py-3 rounded-xl text-[14px] font-bold text-white cursor-pointer transition-all active:scale-[0.97] disabled:opacity-50"
            style={CYAN_BUTTON_STYLE}>
            Suivant
          </button>
        </div>
      </>
    )
  }

  if (step === "time") {
    return (
      <>
        <h3 className="text-[18px] font-bold text-white mb-1 text-center">Heure de fin prévue</h3>
        <p className="text-[12px] text-gray-500 mb-5 text-center">Questionnaire envoyé 30 min plus tard</p>

        <TimeWheelPicker
          hour={hourInput}
          minute={minuteInput}
          onHourChange={setHourInput}
          onMinuteChange={setMinuteInput}
        />

        <div className="grid grid-cols-2 gap-3 mt-5">
          <button onClick={onCancel} disabled={saving}
            className="py-3 rounded-xl text-[14px] font-semibold text-gray-400 cursor-pointer transition-all active:scale-[0.97] disabled:opacity-50"
            style={GHOST_BUTTON_STYLE}>
            Annuler
          </button>
          <SubmitButton onClick={handleSubmitTime} saving={saving} label={hasContentStep ? "Suivant" : "Valider"} />
        </div>
      </>
    )
  }

  return (
    <>
      <h3 className="text-[18px] font-bold text-white mb-1 text-center">Contenu de la séance</h3>
      <p className="text-[12px] text-gray-500 mb-4 text-center">Facultatif — modifiable plus tard depuis l'historique</p>
      <textarea
        value={contentInput}
        onChange={(e) => setContentInput(e.target.value)}
        placeholder={"Ex :\nSquat 4×8 à 80 kg\nTractions 3×10\nGainage 3×45 s"}
        rows={6}
        maxLength={FREE_SESSION_CONTENT_MAX}
        className="w-full px-4 py-3 rounded-xl bg-transparent outline-none text-[14px] text-white placeholder:text-gray-600 resize-none leading-relaxed"
        style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(30,41,59,0.55)", caretColor: "#06b6d4" }}
      />
      <div className="grid grid-cols-2 gap-3 mt-4">
        <button onClick={() => submit(null)} disabled={saving}
          className="py-3 rounded-xl text-[14px] font-semibold text-gray-400 cursor-pointer transition-all active:scale-[0.97] disabled:opacity-50"
          style={GHOST_BUTTON_STYLE}>
          Passer
        </button>
        <SubmitButton onClick={() => submit(contentInput)} saving={saving} label="Valider" />
      </div>
    </>
  )
}

function SubmitButton({ onClick, saving, label }: { onClick: () => void; saving: boolean; label: string }) {
  return (
    <button
      onClick={onClick}
      disabled={saving}
      className="py-3 rounded-xl text-[14px] font-bold text-white cursor-pointer transition-all active:scale-[0.97] disabled:opacity-70 flex items-center justify-center gap-2"
      style={CYAN_BUTTON_STYLE}>
      {saving && <div className="w-3.5 h-3.5 rounded-full border-2 border-white/30 border-t-white animate-spin" />}
      {saving ? "…" : label}
    </button>
  )
}

// ============================================
// SÉANCE LIBRE — parcours complet hors calendrier
// ============================================

interface AddFreeSessionSheetProps {
  open: boolean
  /** Jour de la séance (YYYY-MM-DD). */
  date: string
  onClose: () => void
  /** Création déléguée (ex. `addEvent` de useCalendarEvents, pour garder sa liste à jour). */
  create?: (input: CalendarEventInput) => Promise<{ error: string | null; event: CalendarEventRow | null }>
  onCreated?: (event: CalendarEventRow) => void
}

/**
 * Séance libre ajoutée depuis l'accueil ou l'historique (comptes en suivi seul) : même
 * parcours et même écriture que depuis le calendrier.
 */
export function AddFreeSessionSheet({ open, date, onClose, create, onCreated }: AddFreeSessionSheetProps) {
  const { track } = useAnalytics()
  const [saving, setSaving] = useState(false)

  const handleSubmit = async ({ title, endTime, content }: AddEventValues) => {
    setSaving(true)
    const input: CalendarEventInput = { type: "seance-libre", title, event_date: date, end_time: endTime, content }
    let result: { error: string | null; event: CalendarEventRow | null }
    if (create) {
      result = await create(input)
    } else {
      try {
        result = { error: null, event: await createCalendarEvent(input) }
      } catch (err) {
        result = { error: err instanceof Error ? err.message : "Erreur création", event: null }
      }
    }
    setSaving(false)
    if (result.error || !result.event) {
      toast.error("Erreur", { description: result.error ?? undefined })
      return
    }
    track("calendar_event_created", { type: "seance-libre" })
    toast.success("Séance ajoutée")
    onCreated?.(result.event)
    onClose()
  }

  return (
    <CalendarSheet open={open} onClose={() => !saving && onClose()}>
      {/* Démonté à la fermeture : chaque ouverture repart de la première étape, champs vides. */}
      <AddEventSteps
        date={date}
        type="seance-libre"
        typeLabel="Séance libre"
        saving={saving}
        onCancel={onClose}
        onSubmit={handleSubmit}
      />
    </CalendarSheet>
  )
}

// ============================================
// TIME WHEEL PICKER (scrollable)
// ============================================

interface TimeWheelPickerProps {
  hour: string
  minute: string
  onHourChange: (h: string) => void
  onMinuteChange: (m: string) => void
  recapLabel?: string
}

export function TimeWheelPicker({ hour, minute, onHourChange, onMinuteChange, recapLabel = "Fin prévue" }: TimeWheelPickerProps) {
  const hours = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, "0"))
  const minutes = Array.from({ length: 12 }, (_, i) => String(i * 5).padStart(2, "0"))

  return (
    <div>
      <div className="flex items-center justify-center gap-3 mb-2">
        <div className="text-center">
          <p className="text-[10px] text-gray-500 uppercase tracking-wider mb-1">Heure</p>
          <WheelColumn values={hours} selected={hour} onChange={onHourChange} />
        </div>
        <div className="text-[30px] font-bold text-white mt-5">:</div>
        <div className="text-center">
          <p className="text-[10px] text-gray-500 uppercase tracking-wider mb-1">Minute</p>
          <WheelColumn values={minutes} selected={minute} onChange={onMinuteChange} />
        </div>
      </div>
      <p className="text-center text-[13px] text-cyan-400 font-bold mt-2">
        {recapLabel} : {hour}:{minute}
      </p>
    </div>
  )
}

function WheelColumn({ values, selected, onChange }: { values: string[]; selected: string; onChange: (v: string) => void }) {
  return (
    <div className="h-[120px] w-[80px] overflow-y-auto scrollbar-hide rounded-xl"
      style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(30,41,59,0.55)" }}>
      <div className="py-[40px]">
        {values.map((v) => {
          const isSel = v === selected
          return (
            <button
              key={v}
              onClick={() => onChange(v)}
              className={`w-full py-2 text-center cursor-pointer transition-all ${isSel ? "text-cyan-400 font-bold text-[20px]" : "text-gray-600 text-[14px]"}`}
              style={isSel ? { background: "rgba(6,182,212,0.08)" } : {}}
            >
              {v}
            </button>
          )
        })}
      </div>
    </div>
  )
}
